import { Button, Notice, QCard, Saved, WizardNav } from '@concord/ui';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useId, useState } from 'react';
import { errorMessage } from '../../../lib/api';
import { searchCaseTypes, usePatchIntake } from '../api';
import type { StepProps } from '../IntakeWizard';
import s from './steps.module.css';

/** INT-03: the case type from the managed taxonomy, search first. */
export function CaseTypeStep({ v, next, mode }: StepProps) {
  const patch = usePatchIntake(v.id);
  const [q, setQ] = useState(v.caseType?.term ?? '');
  const [debounced, setDebounced] = useState(q);
  const [active, setActive] = useState(0);
  const listId = useId();
  useEffect(() => {
    const t = setTimeout(() => setDebounced(q), 150);
    return () => clearTimeout(t);
  }, [q]);
  const results = useQuery({
    queryKey: ['case-types', v.id, debounced],
    queryFn: () => searchCaseTypes(debounced === v.caseType?.term ? '' : debounced, v.id),
  });
  const items = results.data ?? [];
  const choose = (item: { id: string; term: string }) => {
    setQ(item.term);
    patch.mutate({ caseTypeId: item.id });
  };
  const who = mode === 'staff' ? 'the patient’s' : 'your';

  return (
    <>
      <QCard
        label="Case type"
        title={mode === 'staff' ? 'What is the case about?' : 'What is your case about?'}
        lead={`Start typing the diagnosis. If ${who === 'your' ? 'you are' : 'they are'} not sure of the exact name, pick the closest; the history can say more.`}
      >
        <div className="field">
          <label htmlFor={`${listId}-input`}>Diagnosis or cancer type</label>
          <input
            id={`${listId}-input`}
            className="input"
            role="combobox"
            aria-expanded={items.length > 0}
            aria-controls={listId}
            aria-autocomplete="list"
            aria-activedescendant={items[active] ? `${listId}-${items[active].id}` : undefined}
            autoComplete="off"
            placeholder="For example: colorectal, myeloma, glioblastoma"
            value={q}
            onChange={(e) => {
              setQ(e.target.value);
              setActive(0);
            }}
            onKeyDown={(e) => {
              if (e.key === 'ArrowDown') setActive((i) => Math.min(items.length - 1, i + 1));
              if (e.key === 'ArrowUp') setActive((i) => Math.max(0, i - 1));
              if (e.key === 'Enter' && items[active]) {
                e.preventDefault();
                choose(items[active]);
              }
            }}
          />
          <span className="hint">
            Covered by {who} plan: {v.entitlement.planName}.
          </span>
        </div>
        <div className={s.options} role="listbox" id={listId} aria-label="Matching case types">
          {items.map((item, i) => {
            const selected = v.caseType?.id === item.id;
            return (
              <div
                key={item.id}
                id={`${listId}-${item.id}`}
                role="option"
                aria-selected={selected}
                tabIndex={-1}
                className={s.opt}
                data-active={i === active || undefined}
                data-on={selected || undefined}
                onClick={() => choose(item)}
                onKeyDown={(e) => e.key === 'Enter' && choose(item)}
              >
                <span className={s.r} />
                <span>
                  {item.term}
                  {item.synonyms.length > 0 && (
                    <span className={s.syn}> · also {item.synonyms.join(', ')}</span>
                  )}
                </span>
              </div>
            );
          })}
          {results.isSuccess && items.length === 0 && (
            <p className="sub2" style={{ margin: 0 }}>
              Nothing matches “{debounced}”. Try a shorter word, or choose “Cancer of unknown primary”.
            </p>
          )}
        </div>
        {patch.isError && <Notice tone="bad">{errorMessage(patch.error)}</Notice>}
      </QCard>
      <WizardNav
        back={<span />}
        status={v.caseType ? <Saved>Saved</Saved> : null}
        next={
          <Button size="lg" onClick={next} disabled={!v.caseType}>
            Continue to expert panel
          </Button>
        }
      />
    </>
  );
}
